import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, CircularProgress,
} from '@mui/material'
import { Add, Download, Delete } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listMyExpenses, createExpense, deleteExpense, getExpenseAttachment, listExpenseCategories,
  COST_CENTERS, type Expense, type CostCenter,
} from '../../api/deconturi'
import { errorMessage } from '../../api/client'

export default function CheltuieliMelePage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['cheltuieli', 'mele'], queryFn: listMyExpenses })

  const deleteMutation = useMutation({
    mutationFn: deleteExpense,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cheltuieli', 'mele'] }),
  })

  const download = async (row: Expense) => {
    const blob = await getExpenseAttachment(row.id)
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const columns = useMemo<GridColDef<Expense>[]>(() => [
    { field: 'date', headerName: 'Dată', width: 110 },
    { field: 'categoryName', headerName: 'Categorie', width: 150 },
    { field: 'description', headerName: 'Descriere', flex: 1, minWidth: 200 },
    { field: 'amount', headerName: 'Sumă', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'expenseReportId', headerName: 'Status', width: 140, sortable: false,
      renderCell: (p) => p.row.expenseReportId
        ? <Chip size="small" label="în decont" color="info" variant="outlined" />
        : <Chip size="small" label="disponibilă" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row">
          <IconButton size="small" onClick={() => void download(p.row)}><Download fontSize="small" /></IconButton>
          {!p.row.expenseReportId && (
            <IconButton size="small" onClick={() => deleteMutation.mutate(p.row.id)}><Delete fontSize="small" /></IconButton>
          )}
        </Stack>
      ),
    },
  ], [deleteMutation])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Cheltuieli</Typography>
        <Typography variant="inherit" color="text.primary">Cheltuielile mele</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Cheltuielile mele</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>Adaugă cheltuială</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <NewExpenseDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Stack>
  )
}

function NewExpenseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: categories } = useQuery({ queryKey: ['financiar', 'categorii-cheltuieli'], queryFn: listExpenseCategories, enabled: open })

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [categoryId, setCategoryId] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [exchangeRate, setExchangeRate] = useState('1')
  const [costCenter, setCostCenter] = useState<CostCenter>('Administrativ')
  const [department, setDepartment] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const reset = () => {
    setDate(new Date().toISOString().slice(0, 10)); setCategoryId(''); setDescription(''); setAmount('');
    setCurrency('RON'); setExchangeRate('1'); setCostCenter('Administrativ'); setDepartment(''); setFile(null)
  }

  const mutation = useMutation({
    mutationFn: () => createExpense({
      date, categoryId, description, amount: Number(amount), currency, exchangeRate: Number(exchangeRate),
      costCenter, department: department || null, file: file!,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'mele'] })
      reset()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cheltuială nouă</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            <TextField select label="Categorie" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} fullWidth>
              {categories?.filter((c) => c.isActive).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Descriere" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 110 }} />
            {currency !== 'RON' && <TextField type="number" label="Curs/RON" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} sx={{ width: 140 }} />}
            <TextField type="number" label="Sumă" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Centru de cost" value={costCenter} onChange={(e) => setCostCenter(e.target.value as CostCenter)} fullWidth>
              {COST_CENTERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Departament" value={department} onChange={(e) => setDepartment(e.target.value)} fullWidth />
          </Stack>
          <Button component="label" variant="outlined">
            {file ? file.name : 'Atașează document justificativ (obligatoriu)'}
            <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!categoryId || !description || !amount || !file || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate()}>
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
